import Vue from "vue";
import Router from "vue-router";
import store from "./store";
import axios from "axios";
import Cookies from "js-cookie";
import { getUserType, homeRouteFor, isSuperAdmin } from "./components/Common/navConfig";
import { USER_PAGES, DUPLICATES_ROUTE, userPage } from "./components/superAdmin/userRoles";

Vue.use(Router);

// Role keys come from components/Common/navConfig.js (roleForUType).
// The super admin may open every route that allows "admin".
const ADMIN = ["admin"];
const DESK = ["clerk", "superintendent", "division"]; // division office staff
const STAFF = DESK.concat(["range"], ADMIN);
const EVERYONE = STAFF.concat(["user"]);

// Shell route helper: every page inside the sidebar layout needs a login and
// a list of roles allowed to open it.
function page(path, name, roles, component, props) {
  const route = { path, name, meta: { requireAuth: true, roles }, component };
  if (props) route.props = props;
  return route;
}

// The super admin manages range and division officers on the users list role
// pages; other admins keep the officer list.
function officerList(path, name, kind, roleKey) {
  return Object.assign(page(path, name, ADMIN, () => import("./components/Common/OfficerList.vue"), { kind }), {
    beforeEnter: (to, from, next) => (isSuperAdmin() ? next(userPage(roleKey).route) : next()),
  });
}

// Pending desk of each office role: one tabbed list for its three stages.
const deskList = () => import("./components/Common/ApplicationList.vue");
const roleDashboard = () => import("./components/Common/RoleDashboard.vue");
const applicationDetail = () => import("./components/Common/ApplicationDetail.vue");
const applicantList = () => import("./components/Common/ApplicantApplications.vue");
const applicantDetail = () => import("./components/Common/ApplicantApplicationDetail.vue");
const userManagement = () => import("./components/superAdmin/UserManagement.vue");

let router = new Router({
  mode: "history",
  // A new page opens at the top (the window scrolls, so it would otherwise
  // keep the last page's position); Back/Forward restore where you were, and
  // query-only changes such as a list's stage tab leave the scroll alone.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.path === from.path) return false;
    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: "/appSidebar",
      props: true,
      component: () => import("./components/Common/appSidebar"),
      meta: { requireAuth: true },
      children: [
        // ── Common ───────────────────────────────────────────
        page("/profilepage", "profilepage", EVERYONE, () => import("./components/Common/profilePage.vue")),
        // Full history with the officers' remarks: staff only (applicants use /trackapplication)
        page("/records", "records", STAFF, () => import("./components/user/RecordsPage.vue")),

        // ── Admin / super admin ──────────────────────────────
        page("/admin-dashboard", "admin-dashboard", ADMIN, roleDashboard, { role: "admin" }),
        // Users list: one page per role (see superAdmin/userRoles.js)
        ...USER_PAGES.map((p) =>
          page(p.route, p.key === "all" ? "superadmin-users" : "superadmin-users-" + p.key, ["superadmin"], userManagement, { role: p.key })
        ),
        page(DUPLICATES_ROUTE, "superadmin-duplicate-accounts", ["superadmin"], () => import("./components/superAdmin/DuplicateAccounts.vue")),
        // Every payment attempt with the reason a failed one failed: super admin only
        page("/payment-log", "payment-log", ["superadmin"], () => import("./components/completedpayments/PaymentLog.vue")),
        // Applications paid through Nammude Keralam (isNamudekeralam): super admin only
        page("/namudekeralam-applications", "namudekeralam-applications", ["superadmin"], () => import("./components/completedpayments/NamudekeralamApplications.vue")),
        page("/admin-profile", "admin-profile", ADMIN, () => import("./components/Common/profilePage.vue")),
        page("/admin-applications", "admin-applications", ADMIN, () => import("./components/Common/AllApplications.vue"), { scope: "admin" }),
        officerList("/range-officers", "range-officers", "range", "773"),
        officerList("/division-officers", "division-officers", "division", "774"),
        page("/AdminFilmDetailedView", "AdminFilmDetailedView", ADMIN.concat(["division"]), applicationDetail, { mode: "admin" }),

        // ── Shared by admin and division ─────────────────────
        page("/admin-payments", "admin-payments", ADMIN.concat(["division"]), () => import("./components/completedpayments/payments.vue")),
        page("/division-code", "division-code", ADMIN.concat(["division"]), () => import("./components/superAdmin/DivisionCode.vue")),
        page("/track-applications", "track-applications", ADMIN.concat(["division"]), () => import("./components/Common/TrackApplication.vue")),
        page("/etreasury-search", "etreasury-search", ADMIN.concat(["division"]), () => import("./components/division/ETreasurySearch.vue")),

        // ── Division (DFO) ───────────────────────────────────
        page("/divisiondashboard", "divisiondashboard", ["division"], roleDashboard, { role: "division" }),
        page("/ApplicationDivision", "ApplicationDivision", ["division"], deskList, { role: "division" }),
        page("/FilmDetailedView", "FilmDetailedView", ["division"], applicationDetail, { mode: "division" }),
        page("/division-applications", "division-applications", ["division"], () => import("./components/Common/AllApplications.vue"), { scope: "division" }),
        page("/RefundedApplications", "RefundedApplications", ["division"], () => import("./components/division/RefundedApplications.vue")),
        page("/cancelrequests", "cancelrequests", ["division"], () => import("./components/division/CancelRequests.vue")),
        page("/employees", "EmployeeList", ["division"], () => import("./components/division/EmployeeList.vue")),

        // ── Section clerk ────────────────────────────────────
        page("/ClerkDashboard", "ClerkDashboard", ["clerk"], roleDashboard, { role: "clerk" }),
        page("/sectionhome", "sectionhome", ["clerk"], deskList, { role: "clerk" }),
        page("/CampDetailedViewClerk", "CampDetailedViewClerk", ["clerk"], applicationDetail, { mode: "clerk" }),

        // ── Superintendent ───────────────────────────────────
        page("/SuperintendentDashboard", "SuperintendentDashboard", ["superintendent"], roleDashboard, { role: "superintendent" }),
        page("/superintedent", "superintedent", ["superintendent"], deskList, { role: "superintendent" }),
        page("/DetailedViewsuperintedent", "DetailedViewsuperintedent", ["superintendent"], applicationDetail, { mode: "superintendent" }),

        // ── Range officer ────────────────────────────────────
        page("/RangeDashboard", "RangeDashboard", ["range"], roleDashboard, { role: "range" }),
        page("/rangebookings", "rangebookings", ["range"], deskList, { role: "range" }),
        page("/CampDetailedView", "CampDetailedView", ["range"], applicationDetail, { mode: "range" }),

        // ── Applicant ────────────────────────────────────────
        page("/booknow", "booknow", ["user"], () => import("./components/user/BookNatureCamp.vue")),
        page("/mybooking", "mybooking", ["user"], applicantList, { mode: "mine" }),
        page("/licenceissued", "licenceissued", ["user"], applicantList, { mode: "licence" }),
        page("/ForwardedApplications", "ForwardedApplications", ["user"], applicantList, { mode: "forwarded" }),
        page("/rejectedapplications", "rejectedapplications", ["user"], applicantList, { mode: "rejected" }),
        page("/cancelledapplications", "cancelledapplications", ["user"], applicantList, { mode: "cancelled" }),
        page("/CancelledApplicationView", "CancelledApplicationView", ["user"], applicantDetail),
        page("/editbooking", "editbooking", ["user"], () => import("./components/user/EditMyBookings.vue")),
        page("/detailedviewapplicant", "detailedviewapplicant", ["user"], applicantDetail),
        page("/trackapplication", "trackapplication", ["user"], () => import("./components/Common/ApplicantTrack.vue")),
        page("/SuccessPage", "SuccessPage", ["user"], () => import("./components/Payment/SuccessPage.vue")),
        page("/FailurePage", "FailurePage", ["user"], () => import("./components/Payment/FailurePage.vue")),
      ],
    },

    // Old per-stage pages now open the matching tab of the role's desk.
    { path: "/T1page", redirect: { path: "/sectionhome", query: { stage: "1" } } },
    { path: "/T4page", redirect: { path: "/sectionhome", query: { stage: "2" } } },
    { path: "/T8page", redirect: { path: "/sectionhome", query: { stage: "3" } } },
    { path: "/t2page", redirect: { path: "/superintedent", query: { stage: "1" } } },
    { path: "/t5page", redirect: { path: "/superintedent", query: { stage: "2" } } },
    { path: "/t9page", redirect: { path: "/superintedent", query: { stage: "3" } } },
    { path: "/t3page", redirect: { path: "/ApplicationDivision", query: { stage: "1" } } },
    { path: "/T6page", redirect: { path: "/ApplicationDivision", query: { stage: "2" } } },
    { path: "/T10page", redirect: { path: "/ApplicationDivision", query: { stage: "3" } } },
    { path: "/cancelview", redirect: (to) => ({ path: "/FilmDetailedView", query: to.query }) },
    { path: "/timeline", redirect: "/trackapplication" },
    { path: "/sectionclerk", redirect: "/employees" },
    { path: "/superintedentdivision", redirect: "/employees" },

    // ── Public ─────────────────────────────────────────────
    {
      path: "/",
      name: "LandingPage",
      meta: { requireAuth: false },
      component: () => import("./components/LandingPage.vue"),
    },
    {
      path: "/login",
      name: "LoginPage",
      meta: { requireAuth: false },
      component: () => import("./components/Login/LoginPage"),
    },
    {
      path: "/register",
      name: "register",
      meta: { requireAuth: false },
      component: () => import("./components/Login/RegisterPage"),
    },
    {
      path: "/forgotpassword",
      name: "forgotpassword",
      meta: { requireAuth: false },
      component: () => import("./components/Login/ForgotPassword"),
    },
    {
      path: "/OtpPage",
      name: "OtpPage",
      meta: { requireAuth: false },
      component: () => import("./components/Login/OtpPage.vue"),
    },
    {
      path: "/verify",
      name: "verify",
      meta: { requireAuth: false },
      component: () => import("./components/Login/onetimePass.vue"),
    },
    {
      path: "/ServerError",
      name: "ServerError",
      props: true,
      component: () => import("./components/Common/500"),
    },
    // Error pages (all drawn by Common/ErrorPage.vue)
    {
      path: "/401",
      name: "Unauthorized",
      meta: { requireAuth: false },
      component: () => import("./components/Common/401"),
    },
    {
      path: "/403",
      name: "Forbidden",
      meta: { requireAuth: false },
      component: () => import("./components/Common/403"),
    },
    {
      // /error/400, /error/408, /error/429, /error/502, /error/503 ...
      path: "/error/:code",
      name: "ErrorPage",
      props: true,
      meta: { requireAuth: false },
      component: () => import("./components/Common/ErrorPage.vue"),
    },
    {
      path: "*",
      name: "404PageNotFound",
      props: true,
      meta: { requireAuth: false },
      component: () => import("./components/Common/404"),
    },
  ],
});

function isAuthorized(route) {
  const role = getUserType();
  return route.matched.every((r) => {
    const roles = r.meta.roles;
    if (!roles) return true;
    if (role === "superadmin" && roles.includes("admin")) return true;
    return roles.includes(role);
  });
}

// Confirms the stored token is still valid, refreshing it from the
// refreshToken cookie if needed. Resolves true when the session is usable.
function verifySession() {
  const token = localStorage.getItem("token");
  return axios({ method: "GET", url: "/token/authenticate", headers: { token } })
    .then((response) => {
      if (response.data.status) return true;
      const refreshToken = Cookies.get("refreshToken");
      if (!refreshToken) return false;
      return axios({ method: "POST", url: "/refresh-token", data: { refreshToken } }).then((refreshResponse) => {
        if (!refreshResponse.data || !refreshResponse.data.token) return false;
        localStorage.setItem("token", refreshResponse.data.token);
        store.commit("loginUser", refreshResponse.data.token);
        if (refreshResponse.data.refreshToken) {
          Cookies.set("refreshToken", refreshResponse.data.refreshToken, { secure: true, sameSite: "strict" });
        }
        return true;
      });
    })
    .catch((err) => {
      console.error(err);
      return false;
    });
}

router.beforeEach((to, from, next) => {
  const needsAuth = to.matched.some((route) => route.meta.requireAuth === true);

  if (!store.state.isLoggedIn) {
    if (needsAuth) next({ name: "LoginPage" });
    else next();
    return;
  }

  // Signed in: the login page sends the user to their own home page.
  if (to.name === "LoginPage") {
    next({ path: homeRouteFor(localStorage.getItem("uType")) });
    return;
  }
  if (!needsAuth) {
    next();
    return;
  }
  // Role check happens before the session check, for every protected page.
  // Signed in but not allowed here: the "access restricted" page.
  if (!isAuthorized(to)) {
    next({ path: "/403" });
    return;
  }
  verifySession().then((ok) => {
    if (ok) {
      next();
    } else {
      next(false);
      store.commit("sessionOut");
    }
  });
});

export default router;
